import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useProgress } from '../context/ProgressContext';
import { ModuleType } from '../types/progress';
import { useResizeObserver } from '../hooks/useResizeObserver';

const topics = [
  {
    id: 'data-structures',
    title: 'Introduction to Data Structures',
    description: 'Learn the fundamental building blocks of programming',
    color: '#00f2fe',
    moduleType: 'dataStructures' as ModuleType,
  },
  {
    id: 'big-o',
    title: 'Big-O Notation',
    description: 'Understanding algorithmic efficiency and complexity',
    color: '#4facfe',
    moduleType: 'bigO' as ModuleType,
  },
  {
    id: 'control-flow',
    title: 'Control Flow',
    description: 'Master program execution and decision making',
    color: '#667eea',
    moduleType: 'controlFlow' as ModuleType,
  },
  {
    id: 'recursion',
    title: 'Recursion',
    description: 'Explore the power of self-referential functions',
    color: '#a18cd1',
    moduleType: 'recursion' as ModuleType,
  },
  {
    id: 'arrays',
    title: 'Arrays',
    description: 'Working with sequential data structures',
    color: '#f093fb',
    moduleType: 'array' as ModuleType,
  },
  {
    id: 'strings',
    title: 'Strings',
    description: 'Manipulating and processing text data',
    color: '#fbc2eb',
    moduleType: 'string' as ModuleType,
  },
  {
    id: 'linked-lists',
    title: 'Linked Lists',
    description: 'Understanding dynamic data structures',
    color: '#ff9a9e',
    moduleType: 'linkedList' as ModuleType,
  },
  {
    id: 'stack-queue',
    title: 'Stacks & Queues',
    description: 'Master fundamental abstract data types',
    color: '#a1c4fd',
    moduleType: 'stackQueue' as ModuleType,
  }
];

const Topics: React.FC = () => {
  const navigate = useNavigate();
  const { getCompletionPercentage, resetAllProgress } = useProgress();
  const [hoveredTopicId, setHoveredTopicId] = useState<string | null>(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [gridHeight, setGridHeight] = useState(0);

  const topicsWithProgress = topics.map(topic => ({
    ...topic,
    progress: Math.round(getCompletionPercentage(topic.moduleType).overall)
  }));

  const gridRef = useResizeObserver((entry) => {
    setGridHeight(entry.contentRect.height);
  });

  useEffect(() => {
    setIsLoading(false);
  }, []);

  const handleTopicClick = useCallback((topicId: string) => {
    navigate(`/topic/${topicId}`);
  }, [navigate]);

  const handleTopicHover = useCallback((topicId: string | null) => {
    setHoveredTopicId(topicId);
  }, []);

  const handleResetProgress = useCallback(() => {
    setShowResetConfirm(true);
  }, []);

  const confirmResetProgress = useCallback(() => {
    resetAllProgress();
    setShowResetConfirm(false);
  }, [resetAllProgress]);

  const cancelResetProgress = useCallback(() => {
    setShowResetConfirm(false);
  }, []);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-2xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden">
      <div className="fixed inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[length:64px_64px] [background-position-x:center]"></div>

      <div className="relative z-10 min-h-screen flex flex-col">
        <div className="relative pt-8 px-4">
          <motion.button
            className="absolute right-4 top-8 text-xs text-white/50 hover:text-white transition-colors flex items-center gap-1"
            onClick={handleResetProgress}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            <span>Reset Progress</span>
          </motion.button>

          <AnimatePresence>
            {showResetConfirm && (
              <motion.div 
                className="fixed inset-0 bg-black/80 flex items-center justify-center z-50"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <motion.div 
                  className="bg-black border border-white/10 p-6 rounded-lg max-w-md w-full mx-4"
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  exit={{ scale: 0.9, opacity: 0 }}
                >
                  <h3 className="text-xl mb-4">Reset Progress</h3>
                  <p className="text-white/70 mb-6">Are you sure you want to reset all progress? This will reset your progress for all topics, including theory, practice, and video content. This action cannot be undone.</p>
                  <div className="flex justify-end gap-3">
                    <button 
                      className="px-4 py-2 text-white/70 hover:text-white transition-colors"
                      onClick={cancelResetProgress}
                    >
                      Cancel
                    </button>
                    <button 
                      className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors"
                      onClick={confirmResetProgress}
                    >
                      Reset All Progress
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          <motion.h1 
            className="text-6xl tracking-tight text-center font-bold mb-12" 
            style={{ fontFamily: '"Space Grotesk", system-ui' }}
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <span className="font-bold">LEARNING</span>
            <span className="font-bold ml-6">PATH</span>
          </motion.h1>
        </div>
        
        <div 
          ref={gridRef as React.RefObject<HTMLDivElement>}
          className="flex-1 w-full max-w-6xl mx-auto px-4 py-8"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <AnimatePresence mode="wait">
              {topicsWithProgress.map((topic, index) => (
                <motion.div
                  key={topic.id}
                  className="relative bg-black/80 backdrop-blur-sm border border-white/10 rounded-xl overflow-hidden"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ 
                    duration: 0.3,
                    delay: index * 0.1,
                    layout: { duration: 0.3 }
                  }}
                  layout
                  onClick={() => handleTopicClick(topic.id)}
                  onMouseEnter={() => handleTopicHover(topic.id)}
                  onMouseLeave={() => handleTopicHover(null)}
                >
                  <div className="p-6 flex flex-col h-full">
                    <div className="flex items-center justify-between mb-4">
                      <div className="text-xs font-medium tracking-wider uppercase" style={{ color: topic.color }}>
                        {topic.id.replace(/-/g, ' ')}
                      </div>
                      <div className="text-sm font-medium text-white">
                        {topic.progress}%
                      </div>
                    </div>
                    
                    <h3 className="text-xl mb-2 font-medium">{topic.title}</h3>
                    <p className="text-white/70 text-sm mb-6 flex-grow">{topic.description}</p>
                    
                    <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden mb-4">
                      <motion.div 
                        className="h-full rounded-full bg-white"
                        initial={{ width: 0 }}
                        animate={{ width: `${topic.progress}%` }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                      />
                    </div>
                  </div>
                  
                  <motion.div 
                    className="absolute inset-0 rounded-xl pointer-events-none"
                    initial={{ opacity: 0 }}
                    animate={{ 
                      opacity: hoveredTopicId === topic.id ? 0.7 : 0,
                      boxShadow: hoveredTopicId === topic.id ? `0 0 30px ${topic.color}40` : "none"
                    }}
                    transition={{ duration: 0.3 }}
                  />
                  
                  <div 
                    className="absolute top-0 left-0 right-0 h-1"
                    style={{ backgroundColor: topic.color }}
                  />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </div>
        
        <motion.div 
          className="flex justify-center pb-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
        >
          <motion.button
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-transparent border border-white/10 text-white rounded-lg transition-colors flex items-center gap-2 hover:bg-white/5"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            <span>Back to Home</span>
          </motion.button>
        </motion.div>
      </div>
    </div>
  );
};

export default Topics;
